/* eslint-disable max-params */
/* eslint-disable @typescript-eslint/naming-convention */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  ViewEncapsulation,
} from '@angular/core';
import {
  DialogSize,
  WcgButtonComponent,
  WcgDialogComponent,
  WcgInputFieldComponent,
} from '@wcg/frontend-framework';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  editIRISIdFormGroup,
  irisIdData,
} from '@data-access/user-management/types';
import { NgTemplateOutlet } from '@angular/common';
import { Store } from '@ngrx/store';
import { USER_MANAGEMENT_ACTIONS } from '@data-access/user-management';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { validatePrimaryIRISDocumentId } from '@data-access/configurations/validators';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    WcgDialogComponent,
    WcgButtonComponent,
    NgTemplateOutlet,
    WcgInputFieldComponent,
  ],
  selector: 'wcg-edit-iris-id-dialog',
  styleUrls: ['./edit-iris-id-dialog.component.css'],
  templateUrl: './edit-iris-id-dialog.component.html',
})
export class EditIrisIdDialogComponent implements AfterViewInit {
  private readonly _matDialogRef = inject(
    MatDialogRef<EditIrisIdDialogComponent>,
  );
  public readonly data = inject<irisIdData & { size: DialogSize }>(
    MAT_DIALOG_DATA,
  );
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _store = inject(Store);
  private readonly _storeActions$ = inject(Actions);
  private readonly _destroyRef = inject(DestroyRef);
  public readonly size = DialogSize.SMALL;

  editIrisIdDialogForm = this._createEditIrisIdDialogForm();
  primaryIRISContactIdNumericError =
    'Primary IRIS ContactID should be a number';
  additionalIRISContactIdNumericError =
    'Additional IRIS ContactID should be a number';

  ngAfterViewInit(): void {
    this._patchEditIrisIdDialogForm();
    this._customListeners();
  }

  close(): void {
    this._matDialogRef.close();
  }

  save(): void {
    if (this.editIrisIdDialogForm.invalid) return;

    this._store.dispatch(
      USER_MANAGEMENT_ACTIONS.updateEditIrisId({
        data: {
          data: {
            additional_legacy_ids:
              this.editIrisIdDialogForm.controls.additional_legacy_ids.value! ??
              [],
          },
          id: this.data.id,
          legacy_id:
            this.editIrisIdDialogForm.controls.legacy_id.value ?? undefined,
        },
      }),
    );
  }

  private _createEditIrisIdDialogForm() {
    return this._formBuilder.group<editIRISIdFormGroup>({
      additional_legacy_ids: new FormControl<string[]>([], {
        nonNullable: true,
        validators: [validatePrimaryIRISDocumentId(/^\d+(,\d+)*$|^$/)],
      }),
      legacy_id: new FormControl<number | null>(null, {
        nonNullable: false,
        validators: [validatePrimaryIRISDocumentId(/^\d+$/)],
      }),
    });
  }

  private _patchEditIrisIdDialogForm(): void {
    this.editIrisIdDialogForm.reset();
    if (this.data) {
      this.editIrisIdDialogForm.patchValue({
        additional_legacy_ids: this.data.additional_legacy_ids ?? [],
        legacy_id: this.data.legacy_id ?? null,
      });
    }
  }

  private _customListeners(): void {
    this._storeActions$
      .pipe(
        ofType(USER_MANAGEMENT_ACTIONS.onUpdateEditIrisIdSuccess),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: () => this._matDialogRef.close(),
      });
  }
}
